/* global document */
import { getDevicePropertyLatest, getChartDevice } from '@/api/device-chart'
import { createRequestCancellation, isRequestCanceled } from '@/api/http'
import { responseBody } from './device-chart-data'
import { chartHost } from './device-chart-runtime'
import { deviceValueQuery, latestValueSample, realtimeValueSample, deviceValueData } from './device-value-data'
import { isStaticDataPreview, sharedReadOptions, readonlyDeviceStream } from './shared-view'
import { pageDevicePending, pageDeviceSkeleton } from './page-bound-placeholder'
import { usesPageContextText, pageProductTextKey, queuePageProductText, stopPageProductText } from './page-product-text'
import { pageDevicePropertyStream, usesPageDeviceProperties } from './page-device-properties'

const states = new WeakMap()
const names = ['avue-echart-text', 'avue-echart-gauge']
const ownValue = vm => names.includes(vm.$options.name)
const kindOf = vm => vm.$options.name === names[0] ? 'text' : 'gauge'

function stateOf (vm) {
  if (!states.has(vm)) states.set(vm, { sequence: 0 })
  return states.get(vm)
}

function bindingKey (vm) {
  if (usesPageContextText(vm)) return 'pageContext:' + pageProductTextKey(vm, chartHost(vm))
  if (Number(vm.dataType) !== 2) return String(vm.dataType)
  const host = chartHost(vm), context = host.navigationContext || {}
  return JSON.stringify([vm.dataType, vm.parameter, vm.data, context.deviceId, context.productId,
    !!host.menuFlag, !!host.isPreview, !!host.isSharedDataView, isStaticDataPreview(host), vm.dataFormatter && vm.dataFormatter.toString()])
}

function unsubscribe (state) {
  const subscription = state.subscription
  state.subscription = null
  if (subscription) { try { subscription.unsubscribe() } catch (error) { /* 旧 helper 在断线后可能无法发送 unsub */ } }
}

function feedback (vm) {
  const state = stateOf(vm)
  if (!vm.$el || typeof document === 'undefined') return
  if (state.overlay && state.overlay.parentNode) state.overlay.parentNode.removeChild(state.overlay)
  state.overlay = null
  vm.$el.setAttribute('aria-busy', String(state.status === 'loading'))
  if (state.status === 'ready' || pageDeviceSkeleton(vm.parameter, state.status)) return
  const overlay = document.createElement('div'), label = document.createElement('span')
  overlay.className = 'device-chart-feedback'
  overlay.setAttribute('role', state.status === 'error' ? 'alert' : 'status')
  label.textContent = state.status === 'loading' ? '正在读取设备属性…' : state.status === 'waiting' ? '等待设备属性上报…'
    : state.status === 'empty' ? '暂无属性数据' : state.error
  overlay.appendChild(label)
  if (state.status === 'error') {
    const retry = document.createElement('button')
    retry.type = 'button'; retry.textContent = '重试'
    retry.setAttribute('data-device-chart-retry', '')
    retry.onclick = () => vm.updateData()
    overlay.appendChild(retry)
  }
  vm.$el.appendChild(overlay); state.overlay = overlay
}

function clearValue (vm, skeleton = false) {
  vm.dataChart = { ...(vm.data || {}), value: skeleton && kindOf(vm) === 'text' ? '—' : '' }
  if (vm.myChart) {
    vm.myChart.clear()
    if (skeleton) {
      vm.updateChart()
      vm.myChart.off('click')
      if (vm.bindClick) vm.bindClick()
    }
  }
}

function pendingValue (vm, host) {
  if (!pageDevicePending(vm.parameter, host.navigationContext || {}, !!host.menuFlag)) return false
  const state = stateOf(vm)
  // 未绑定设备可占位，但缺属性仍是配置问题，不能用占位掩盖。
  const property = vm.parameter && vm.parameter.property
  if (property === undefined || property === null || property === '') {
    state.status = 'error'; state.error = '请选择设备属性'
  } else {
    state.status = 'pending'; state.error = ''
  }
  clearValue(vm, true)
  feedback(vm)
  return true
}

export function stopDeviceValue (vm) {
  const state = stateOf(vm)
  state.sequence++
  clearTimeout(state.timer); clearTimeout(state.paintTimer); clearInterval(vm.checkChart)
  state.timer = null; state.paintTimer = null
  if (state.cancellation) state.cancellation.cancel('设备或属性已切换')
  state.cancellation = null
  unsubscribe(state)
  if (state.overlay && state.overlay.parentNode) state.overlay.parentNode.removeChild(state.overlay)
  state.overlay = null
  if (vm.$el && vm.$el.removeAttribute) vm.$el.removeAttribute('aria-busy')
}

export async function updateDeviceValue (vm) {
  stopDeviceValue(vm)
  const state = stateOf(vm), sequence = state.sequence, binding = bindingKey(vm), host = chartHost(vm)
  const current = () => !vm._isDestroyed && sequence === state.sequence && Number(vm.dataType) === 2 && binding === bindingKey(vm)
  let latest = null, halted = false
  const fail = error => {
    if (!current() || halted || isRequestCanceled(error)) return
    halted = true
    clearTimeout(state.paintTimer); state.paintTimer = null
    if (state.cancellation) state.cancellation.cancel('属性读取已停止')
    unsubscribe(state)
    state.status = 'error'; state.error = (error && error.message) || '设备属性读取失败，请重试'; feedback(vm)
  }
  const publish = () => {
    if (!current() || halted) return
    try {
      const data = deviceValueData(kindOf(vm), latest, vm.parameter, vm.data, vm.dataFormatter, !!host.isPreview, vm.option)
      if (data) {
        vm.dataChart = data
        if (vm.myChart) {
          vm.updateChart()
          vm.myChart.off('click')
          if (vm.bindClick) vm.bindClick()
        }
      } else clearValue(vm, vm.parameter && vm.parameter.deviceSource === 'page')
      state.status = data ? 'ready' : 'empty'; state.error = ''; feedback(vm)
    } catch (error) { fail(error) }
  }
  const accept = sample => {
    if (!sample || (latest && sample.timestamp < latest.timestamp)) return
    const first = !latest
    latest = sample
    // 首值直接显示；后续高频推送合并绘制，不等待另一次 HTTP 初始化。
    if (first) publish()
    else if (!state.paintTimer) state.paintTimer = setTimeout(() => { state.paintTimer = null; publish() }, 100)
  }
  if (pendingValue(vm, host)) return
  clearValue(vm, vm.parameter && vm.parameter.deviceSource === 'page')
  state.status = 'loading'; state.error = ''; feedback(vm)
  try {
    const query = deviceValueQuery(vm.parameter, host.navigationContext || {}, !!host.menuFlag)
    state.cancellation = createRequestCancellation()
    const options = sharedReadOptions(host, { cancelToken: state.cancellation.token })
    if (isStaticDataPreview(host)) {
      // 安全预览维持只读 HTTP 白名单，不为此开放 WS 或执行处理脚本。
      const response = await getDevicePropertyLatest(query.deviceId, query.property, options)
      if (!current()) return
      latest = latestValueSample(responseBody(response), query)
      publish()
      return
    }
    let stream
    if (usesPageDeviceProperties(vm.parameter, host, query.deviceId)) stream = pageDevicePropertyStream(host, query.deviceId, query.property)
    else {
      let productId = vm.parameter.productId
      if (vm.parameter.deviceSource === 'page' || !productId) {
        const body = responseBody(await getChartDevice(query.deviceId, options))
        if (!current()) return
        productId = body.result && body.result.productId
      }
      if (!productId) throw new Error('设备没有产品信息，无法订阅实时属性')
      // 运行态的固定设备仍沿用原单属性通道；编辑画布按设备共享。
      stream = readonlyDeviceStream(host, `device-value-${vm._uid}-${Date.now()}-${sequence}`,
        `/dashboard/device/${productId}/${query.property}/realTime`, { deviceId: query.deviceId, history: 1 })
    }
    state.subscription = stream.subscribe(response => {
      if (!current() || halted) return
      try { accept(realtimeValueSample(response, query)) } catch (error) { fail(error) }
    }, error => fail(error || new Error('实时属性订阅失败，请重试')),
    () => fail(new Error('实时属性订阅已结束，请重试')))
    if (state.subscription.ready) await state.subscription.ready
    if (halted) unsubscribe(state) // 兼容 subscribe 同步报错/结束。
    else if (current() && !latest) {
      // 没有历史记录时并不代表查询失败，也不能永远显示“读取中”。
      state.status = 'waiting'; feedback(vm)
    }
  } catch (error) { fail(error) }
}

export function queueDeviceValue (vm) {
  stopDeviceValue(vm)
  // 仅进入设备分支时释放原自定义 WS，不能取消刚启动的其他数据类型。
  if (vm.sub) { try { vm.sub.unsubscribe() } catch (error) { /* 已关闭 */ } vm.sub = null }
  const state = stateOf(vm)
  if (pendingValue(vm, chartHost(vm))) return
  clearValue(vm, vm.parameter && vm.parameter.deviceSource === 'page')
  state.status = 'loading'; state.error = ''; feedback(vm)
  state.timer = setTimeout(() => updateDeviceValue(vm), 100)
}

export function installDeviceValueRuntime (Vue) {
  if (Vue.__deviceValueRuntime) return
  Vue.__deviceValueRuntime = true
  Vue.mixin({
    beforeCreate () {
      if (!ownValue(this)) return
      const original = this.$options.methods.updateData
      this.$options.methods = { ...this.$options.methods, updateData (...args) {
        if (usesPageContextText(this)) {
          stopDeviceValue(this)
          return queuePageProductText(this, chartHost(this))
        }
        stopPageProductText(this)
        if (Number(this.dataType) === 2) return queueDeviceValue(this)
        stopDeviceValue(this)
        return original.apply(this, args)
      } }
    },
    created () {
      if (!ownValue(this)) return
      this.$watch(() => bindingKey(this), () => { this.updateData() })
    },
    mounted () {
      if (!ownValue(this)) return
      if (usesPageContextText(this)) this.updateData()
      else if (Number(this.dataType) === 2) queueDeviceValue(this)
    },
    beforeDestroy () { if (ownValue(this)) { stopPageProductText(this); stopDeviceValue(this) } }
  })
}
